import { COUNTRIES, COUNTRY_NAME_BY_CODE } from '../countries/index.js';
import { RETRACED_ROLE_KEYS } from '../permissions/index.js';

export const COMPANY_CORE_INFO_TEMPLATES = {
  ABOUT: 'ABOUT',
  OTHER_IDS: 'OTHER_IDS',
  OPERATIONAL_METRICS: 'OPERATIONAL_METRICS',
} as const;

export const COMPANY_CORE_INFO_TEMPLATES_ENUMS = Object.keys(
  COMPANY_CORE_INFO_TEMPLATES,
) as Array<keyof typeof COMPANY_CORE_INFO_TEMPLATES>;

/** Pairs a caller's connectionType with the reciprocal type on the other side of a link. */
export const CONNECTION_TYPES_PAIR: Record<string, string> = {
  SUPPLIER: 'CLIENT',
  CLIENT: 'SUPPLIER',
};

/**
 * Data point type whose value is assigned by the platform, never supplied by a
 * user — e.g. `retraced_id`, which is the company's own id.
 */
export const SYSTEM_DATA_POINT_TYPE = 'SYSTEM';

/** Data point type holding facts about the company itself, shared across every
 * network scope, editable only by the company owner or an admin. */
export const GLOBAL_DATA_POINT_TYPE = 'GLOBAL';

/**
 * Whether a single data point may be edited by the current caller.
 *
 * This is the one place the rule lives — it drives BOTH the `isEditable` flag
 * the dashboards render from and the `isEditable` argument handed to
 * data-points-lib's `validate()`, which rejects a changed value when it is
 * false. The two must agree: a field the UI paints as editable but the API
 * then refuses is worse than one that was never editable.
 *
 * - `SYSTEM` — never editable, whatever the caller's permissions. The platform
 *   assigns these; the write paths that legitimately set them (company
 *   creation) pass `isEditable: true` to `validate()` directly rather than
 *   going through here.
 * - `GLOBAL` — editable only if the caller may edit the company's global data
 *   points, which is what `canEditGlobals` carries.
 * - everything else (`RELATIONAL`, `TRANSACTIONAL`) — relationship-scoped data
 *   belonging to the caller, always editable by them.
 *
 * Before `SYSTEM` existed, callers wrote `type === 'GLOBAL' ? canEdit : true`,
 * which sends a SYSTEM data point down the `: true` branch and makes it MORE
 * editable than a GLOBAL one. Use this helper instead of reintroducing that
 * ternary.
 */
export function isDataPointEditable(
  dataPointType: string | undefined,
  canEditGlobals: boolean,
): boolean {
  if (dataPointType === SYSTEM_DATA_POINT_TYPE) return false;
  if (dataPointType === GLOBAL_DATA_POINT_TYPE) return canEditGlobals;
  return true;
}

/**
 * RELATIONAL data points copied from a company's own defaults when building
 * a network profile of it for a newly-linked counterpart.
 */
export const RELATIONAL_DATA_POINTS_TO_COPY = [
  'display_name',
  'transliterated_display_name',
  'facility_processes',
  'product_levels',
  'product_types',
  'image_list',
  'description',
  'phone',
  'video_file',
  'company_size',
  'fac_email',
];

/**
 * Job-queue name the `CompanyDuplicateReviewJobExecutor` listens on. Must
 * stay in sync with backend's `JOB_NAMES.COMPANIES_DUPLICATE_REVIEW` (which
 * resolves to this same value) and the registered `queue_job_event_processors`
 * row. Modules can't import `@src` constants, so the value is mirrored here.
 */
export const COMPANY_DUPLICATE_REVIEW_JOB_NAME = 'companies:duplicate_review';

const AUDIT_META_KEYS = [
  'createdAt',
  'createdByImpersonateId',
  'createdByUserId',
  'createdByCompanyId',
  'createdByApiKeyId',
  'createdByApiKeyLabel',
  'updatedAt',
  'updatedByImpersonateId',
  'updatedByImpersonateName',
  'updatedByUserId',
  'updatedByUserName',
  'updatedByCompanyId',
  'updatedByCompanyName',
  'updatedByApiKeyId',
  'updatedByApiKeyLabel',
] as const;

export type CompanyAuditMeta = {
  [K in (typeof AUDIT_META_KEYS)[number]]: unknown;
};

export function extractUserAuditMetaFromCtx(
  userMeta: Record<string, unknown> = {},
): CompanyAuditMeta {
  return AUDIT_META_KEYS.reduce((acc, key) => {
    acc[key] = (userMeta[key] ?? null) as CompanyAuditMeta[typeof key];
    return acc;
  }, {} as CompanyAuditMeta);
}

/**
 * `verificationMeta` is a transient, request-only hint sent by the client to
 * identify the audit a promoted answer came from (see D-008 / amfori provenance).
 * It is consumed server-side by `stampAmforiProvenance` to derive the persisted
 * `_meta` block and is NOT a recognised sibling of `value` in the data-points-lib
 * schema (which is strict: `additionalProperties: false`, only `value` + `_meta`).
 * Strip it before any `dataPoints.validate()` call and before persistence.
 */
export function omitVerificationMeta<T extends Record<string, unknown>>(
  entry: T,
): T {
  if (!entry || typeof entry !== 'object' || !('verificationMeta' in entry)) {
    return entry;
  }
  const { verificationMeta: _omit, ...rest } = entry;
  return rest as T;
}

/**
 * Strips the two server-managed siblings of `value` that must NOT be subject to
 * data-points-lib value validation:
 *
 *  - `verificationMeta`: a transient, request-only hint identifying the audit /
 *    assessment a promoted answer came from. Consumed server-side by the
 *    provenance stamper; never persisted, never a recognised schema field.
 *  - `_meta`: the persisted provenance block (`source`, `syncedAt`,
 *    `sourceAuditId`, plus `generatedBy` / `auditType` / `externalSource`). It is
 *    stamped onto the answer AFTER validation on the write path, so re-validating
 *    it on a later save is asymmetric — the value schema's strict `_meta`
 *    sub-schema only permits a subset of those keys and would reject the whole
 *    template. Stripping it here keeps validation symmetric with the write.
 *
 * This strips for validation ONLY: callers pass the result to
 * `dataPoints.validate()` while persisting the original entry, so `_meta`
 * survives untouched in the stored answer.
 */
export function omitMetaForValidation<T extends Record<string, unknown>>(
  entry: T,
): T {
  if (!entry || typeof entry !== 'object') {
    return entry;
  }
  const {
    verificationMeta: _v,
    _meta: _m,
    ...rest
  } = entry as Record<string, unknown>;
  return rest as T;
}

export { getCountryCallingCode } from '../countries/index.js';

/** alpha-2 → English name, from the shared country list. */
export const COUNTRIES_MAPPING: Readonly<Record<string, string>> =
  COUNTRY_NAME_BY_CODE;

export function compareValues(first: unknown, second: unknown): boolean {
  if (typeof first === 'string' && typeof second === 'string')
    return first === second;
  if (
    typeof first === 'object' &&
    typeof second === 'object' &&
    first !== null &&
    second !== null
  ) {
    return deepEqual(
      first as Record<string, unknown>,
      second as Record<string, unknown>,
    );
  }
  return false;
}

function deepEqual(
  obj1: Record<string, unknown>,
  obj2: Record<string, unknown>,
): boolean {
  if (obj1 === obj2) return true;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (
      !deepEqual(
        obj1[key] as Record<string, unknown>,
        obj2[key] as Record<string, unknown>,
      )
    )
      return false;
  }
  return true;
}

type GeoJsonFeature = {
  type: string;
  geometry: { type: string; coordinates: [number, number] };
};

export const COUNTRIES_GEO_LOCATION: Record<string, GeoJsonFeature> =
  COUNTRIES.reduce(
    (acc, obj) => {
      acc[obj.countryCode] = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [obj.center[1], obj.center[0]] as [number, number],
        },
      };
      return acc;
    },
    {} as Record<string, GeoJsonFeature>,
  );

export const GEO_BOUNDING_BOX_PADDING_DEG = 1;

// Approximate land bounding boxes per ISO 3166-1 alpha-2 country code, as [west, south, east, north]
export const COUNTRY_BOUNDING_BOXES: Record<
  string,
  [number, number, number, number]
> = {
  AD: [1.4, 42.4, 1.8, 42.7],
  AF: [60.5, 29.3, 75.2, 38.5],
  AG: [-61.9, 16.9, -61.7, 17.7],
  AI: [-63.2, 18.1, -62.9, 18.3],
  AL: [19.3, 39.6, 21.1, 42.7],
  AM: [43.4, 38.8, 46.6, 41.3],
  AO: [11.7, -18.1, 24.1, -4.4],
  AQ: [-180, -90, 180, -60],
  AR: [-73.6, -55.1, -53.6, -21.8],
  AS: [-171.1, -14.6, -168.1, -11.0],
  AT: [9.5, 46.4, 17.2, 49.0],
  AU: [112.9, -43.7, 153.7, -10.0],
  AW: [-70.1, 12.4, -69.8, 12.7],
  AX: [19.3, 59.7, 21.4, 60.6],
  AZ: [44.8, 38.4, 50.4, 41.9],
  AN: [-69.2, 11.9, -62.9, 18.1],
  BA: [15.7, 42.5, 19.6, 45.3],
  BB: [-59.7, 13.0, -59.4, 13.4],
  BD: [88.0, 20.6, 92.7, 26.7],
  BE: [2.5, 49.5, 6.4, 51.5],
  BF: [-5.5, 9.4, 2.4, 15.1],
  BG: [22.4, 41.2, 28.6, 44.2],
  BH: [50.4, 25.8, 50.7, 26.3],
  BI: [29.0, -4.5, 30.8, -2.3],
  BJ: [0.8, 6.2, 3.9, 12.4],
  BL: [-62.9, 17.8, -62.8, 17.9],
  BM: [-64.9, 32.2, -64.6, 32.4],
  BN: [114.0, 4.0, 115.4, 5.1],
  BO: [-69.7, -22.9, -57.5, -9.7],
  BQ: [-68.5, 12.0, -62.9, 17.7],
  BR: [-74.0, -33.8, -34.8, 5.3],
  BT: [88.7, 26.7, 92.1, 28.4],
  BV: [3.2, -54.5, 3.5, -54.3],
  BW: [20.0, -26.9, 29.4, -17.8],
  BY: [23.2, 51.3, 32.8, 56.2],
  BZ: [-89.2, 15.9, -87.8, 18.5],
  BS: [-79.0, 20.9, -72.7, 27.3],
  CA: [-141.0, 41.7, -52.6, 83.1],
  CH: [5.9, 45.8, 10.5, 47.8],
  CI: [-8.6, 4.4, -2.5, 10.7],
  CL: [-75.7, -55.9, -66.4, -17.5],
  CM: [8.5, 1.7, 16.2, 13.1],
  CN: [73.5, 18.2, 134.8, 53.6],
  CO: [-79.0, -4.2, -66.9, 12.5],
  CR: [-85.9, 8.0, -82.6, 11.2],
  CU: [-84.9, 19.8, -74.1, 23.3],
  CV: [-25.4, 14.8, -22.7, 17.2],
  CW: [-69.2, 12.0, -68.7, 12.4],
  CX: [105.5, -10.6, 105.7, -10.4],
  CY: [32.2, 34.5, 34.6, 35.7],
  CC: [96.8, -12.2, 96.9, -12.1],
  CF: [14.4, 2.2, 27.5, 11.0],
  CK: [-166.0, -22.0, -157.3, -8.9],
  CG: [11.2, -5.0, 18.6, 3.7],
  CZ: [12.1, 48.6, 18.9, 51.1],
  CD: [12.2, -13.5, 31.3, 5.4],
  DE: [5.8, 47.2, 15.1, 55.1],
  DJ: [41.7, 10.9, 43.4, 12.7],
  DK: [8.0, 54.5, 15.2, 57.8],
  DM: [-61.5, 15.2, -61.2, 15.6],
  DZ: [-8.7, 18.9, 12.0, 37.1],
  DO: [-72.0, 17.5, -68.3, 20.0],
  EC: [-92.0, -5.0, -75.2, 1.5],
  EE: [21.7, 57.5, 28.2, 59.7],
  EG: [24.7, 22.0, 36.9, 31.7],
  EH: [-17.1, 20.8, -8.7, 27.7],
  ER: [36.4, 12.4, 43.1, 18.0],
  ES: [-18.3, 27.6, 4.3, 43.8],
  ET: [33.0, 3.4, 48.0, 14.9],
  FI: [20.5, 59.8, 31.6, 70.1],
  FJ: [-180, -21.0, 180, -12.0],
  FM: [137.0, 1.0, 163.0, 10.0],
  FR: [-5.2, 41.3, 9.6, 51.1],
  FK: [-61.3, -52.4, -57.7, -51.0],
  FO: [-7.7, 61.4, -6.3, 62.4],
  GA: [8.7, -3.9, 14.5, 2.3],
  GD: [-61.8, 11.9, -61.4, 12.3],
  GE: [40.0, 41.0, 46.7, 43.6],
  GF: [-54.6, 2.1, -51.6, 5.8],
  GG: [-2.7, 49.4, -2.5, 49.5],
  GH: [-3.3, 4.7, 1.2, 11.2],
  GI: [-5.4, 36.1, -5.3, 36.2],
  GL: [-73.0, 59.7, -11.3, 83.7],
  GN: [-15.1, 7.2, -7.6, 12.7],
  GP: [-61.8, 15.8, -61.0, 16.5],
  GQ: [5.6, -1.5, 11.4, 3.8],
  GR: [19.3, 34.8, 28.3, 41.8],
  GS: [-38.3, -54.9, -35.8, -53.9],
  GT: [-92.3, 13.7, -88.2, 17.8],
  GU: [144.6, 13.2, 145.0, 13.7],
  GW: [-16.8, 10.9, -13.6, 12.7],
  GY: [-61.4, 1.2, -56.5, 8.6],
  GM: [-16.9, 13.0, -13.8, 13.9],
  GB: [-8.6, 49.9, 1.8, 60.9],
  HK: [113.8, 22.1, 114.5, 22.6],
  HN: [-89.4, 12.9, -83.1, 16.5],
  HR: [13.4, 42.4, 19.5, 46.6],
  HT: [-74.5, 18.0, -71.6, 20.1],
  HU: [16.1, 45.7, 22.9, 48.6],
  HM: [73.2, -53.2, 73.7, -52.9],
  ID: [95.0, -11.1, 141.1, 6.1],
  IE: [-10.5, 51.4, -6.0, 55.4],
  IL: [34.2, 29.4, 35.9, 33.4],
  IM: [-4.9, 54.0, -4.3, 54.4],
  IN: [68.1, 6.5, 97.4, 35.7],
  IQ: [38.8, 29.0, 48.6, 37.4],
  IR: [44.0, 25.0, 63.4, 39.8],
  IS: [-24.6, 63.3, -13.5, 66.6],
  IT: [6.6, 35.4, 18.6, 47.1],
  IO: [71.0, -7.4, 72.5, -5.0],
  JE: [-2.3, 49.1, -2.0, 49.3],
  JM: [-78.4, 17.7, -76.2, 18.6],
  JO: [34.9, 29.1, 39.3, 33.4],
  JP: [122.9, 24.0, 146.0, 45.6],
  KE: [33.9, -4.7, 41.9, 5.0],
  KG: [69.3, 39.2, 80.3, 43.3],
  KH: [102.3, 10.4, 107.6, 14.7],
  KP: [124.2, 37.7, 130.7, 43.0],
  KR: [125.9, 33.2, 129.6, 38.6],
  KI: [-180, -12.0, 180, 6.0],
  KN: [-62.9, 17.1, -62.5, 17.4],
  KW: [46.5, 28.5, 48.4, 30.1],
  KZ: [46.5, 40.6, 87.4, 55.4],
  KM: [43.2, -12.4, 44.5, -11.4],
  KY: [-81.4, 19.2, -79.7, 19.8],
  LB: [35.1, 33.0, 36.6, 34.7],
  LC: [-61.1, 13.7, -60.9, 14.1],
  LI: [9.4, 47.0, 9.6, 47.3],
  LK: [79.6, 5.9, 81.9, 9.9],
  LR: [-11.5, 4.3, -7.4, 8.6],
  LS: [27.0, -30.7, 29.5, -28.6],
  LT: [20.9, 53.9, 26.8, 56.5],
  LU: [5.7, 49.4, 6.5, 50.2],
  LV: [21.0, 55.7, 28.2, 58.1],
  LY: [9.3, 19.5, 25.2, 33.2],
  LA: [100.1, 13.9, 107.7, 22.5],
  MA: [-13.2, 27.7, -1.0, 35.9],
  MC: [7.4, 43.7, 7.5, 43.8],
  ME: [18.4, 41.8, 20.4, 43.6],
  MF: [-63.2, 18.0, -63.0, 18.1],
  MG: [43.2, -25.6, 50.5, -11.9],
  ML: [-12.3, 10.1, 4.3, 25.0],
  MM: [92.2, 9.6, 101.2, 28.5],
  MN: [87.7, 41.5, 119.9, 52.2],
  MO: [113.5, 22.1, 113.6, 22.2],
  MQ: [-61.2, 14.4, -60.8, 14.9],
  MR: [-17.1, 14.7, -4.8, 27.3],
  MS: [-62.3, 16.6, -62.1, 16.8],
  MT: [14.1, 35.8, 14.6, 36.1],
  MU: [57.3, -20.6, 63.5, -10.3],
  MV: [72.6, -0.7, 73.8, 7.1],
  MW: [32.7, -17.2, 35.9, -9.4],
  MX: [-118.5, 14.5, -86.7, 32.8],
  MY: [99.6, 0.8, 119.3, 7.4],
  MZ: [30.2, -26.9, 40.9, -10.5],
  MD: [26.6, 45.4, 30.2, 48.5],
  MK: [20.4, 40.8, 23.0, 42.4],
  MH: [165.5, 4.5, 172.0, 14.7],
  MP: [145.1, 14.1, 146.1, 20.6],
  NA: [11.7, -29.0, 25.3, -16.9],
  NC: [163.5, -22.7, 168.2, -19.5],
  NF: [167.9, -29.1, 168.0, -29.0],
  NG: [2.7, 4.3, 14.7, 13.9],
  NI: [-87.7, 10.7, -83.1, 15.0],
  NO: [4.6, 57.9, 31.1, 71.2],
  NP: [80.0, 26.3, 88.2, 30.4],
  NR: [166.9, -0.6, 167.0, -0.5],
  NU: [-170.0, -19.2, -169.8, -19.0],
  NZ: [166.4, -47.3, 178.6, -34.4],
  NL: [3.3, 50.7, 7.3, 53.6],
  NE: [0.2, 11.7, 16.0, 23.5],
  OM: [52.0, 16.6, 59.8, 26.4],
  PA: [-83.1, 7.2, -77.1, 9.7],
  PE: [-81.4, -18.4, -68.7, 0.0],
  PF: [-152.0, -27.7, -134.0, -7.9],
  PG: [140.8, -11.7, 156.0, -1.3],
  PK: [60.9, 23.7, 77.8, 37.1],
  PL: [14.1, 49.0, 24.2, 54.9],
  PM: [-56.5, 46.7, -56.1, 47.1],
  PN: [-130.8, -25.1, -124.7, -23.9],
  PR: [-67.3, 17.9, -65.2, 18.5],
  PS: [34.2, 31.2, 35.6, 32.6],
  PT: [-31.3, 32.4, -6.2, 42.2],
  PW: [131.1, 2.8, 134.7, 8.1],
  PY: [-62.7, -27.6, -54.2, -19.3],
  PH: [116.9, 4.6, 126.6, 21.1],
  QA: [50.7, 24.5, 51.7, 26.2],
  RE: [55.2, -21.4, 55.8, -20.9],
  RO: [20.2, 43.6, 29.7, 48.3],
  RS: [18.8, 42.2, 23.0, 46.2],
  RU: [19.6, 41.1, 180.0, 81.9],
  RW: [28.8, -2.9, 30.9, -1.0],
  SA: [34.5, 16.3, 55.7, 32.2],
  SB: [155.5, -11.9, 167.0, -6.6],
  SC: [46.2, -9.8, 56.3, -3.7],
  SE: [11.0, 55.3, 24.2, 69.1],
  SG: [103.6, 1.2, 104.1, 1.5],
  SH: [-14.5, -16.1, -5.6, -7.8],
  SI: [13.3, 45.4, 16.6, 46.9],
  SJ: [10.0, 74.0, 35.0, 81.0],
  SK: [16.8, 47.7, 22.6, 49.7],
  SL: [-13.4, 6.9, -10.2, 10.0],
  SM: [12.4, 43.8, 12.5, 44.0],
  SN: [-17.6, 12.2, -11.3, 16.7],
  SO: [40.9, -1.7, 51.4, 12.0],
  SR: [-58.1, 1.8, -53.9, 6.0],
  SS: [24.1, 3.4, 35.9, 12.2],
  ST: [6.4, 0.0, 7.5, 1.7],
  SV: [-90.1, 13.1, -87.7, 14.5],
  SX: [-63.1, 18.0, -63.0, 18.1],
  SY: [35.7, 32.3, 42.4, 37.3],
  SZ: [30.7, -27.4, 32.2, -25.7],
  SD: [21.8, 8.6, 38.6, 22.2],
  TD: [13.5, 7.4, 24.0, 23.5],
  TG: [-0.1, 6.1, 1.8, 11.1],
  TH: [97.3, 5.6, 105.6, 20.5],
  TJ: [67.3, 36.7, 75.2, 41.0],
  TK: [-172.5, -9.5, -171.2, -8.5],
  TL: [124.0, -9.5, 127.3, -8.1],
  TM: [52.4, 35.1, 66.7, 42.8],
  TN: [7.5, 30.2, 11.6, 37.5],
  TO: [-176.0, -23.0, -173.0, -15.0],
  TR: [25.6, 35.8, 44.8, 42.1],
  TT: [-61.9, 10.0, -60.5, 11.3],
  TV: [-180, -11.0, 180, -5.0],
  TZ: [29.3, -11.8, 40.4, -1.0],
  TW: [119.3, 21.9, 122.0, 25.3],
  TC: [-72.5, 21.3, -71.1, 21.9],
  TF: [50.1, -49.8, 70.6, -37.8],
  UA: [22.1, 44.4, 40.2, 52.4],
  UG: [29.5, -1.5, 35.0, 4.2],
  US: [-179.2, 18.9, -66.9, 71.4],
  UY: [-58.5, -35.0, -53.1, -30.1],
  UZ: [55.9, 37.1, 73.2, 45.6],
  UM: [-180, -1.0, 180, 29.0],
  VC: [-61.5, 12.5, -61.1, 13.4],
  VE: [-73.4, 0.6, -59.8, 12.2],
  VG: [-64.8, 18.3, -64.3, 18.8],
  VI: [-65.1, 17.6, -64.5, 18.4],
  VN: [102.1, 8.2, 109.5, 23.4],
  VU: [166.5, -20.3, 170.2, -13.0],
  VA: [12.44, 41.89, 12.46, 41.91],
  WF: [-178.3, -14.4, -176.0, -13.2],
  WS: [-172.8, -14.1, -171.4, -13.4],
  XK: [20.0, 41.8, 21.8, 43.3],
  YE: [42.5, 12.1, 54.5, 19.0],
  YT: [45.0, -13.1, 45.3, -12.6],
  ZA: [16.3, -34.9, 32.9, -22.1],
  ZM: [21.9, -18.1, 33.7, -8.2],
  ZW: [25.2, -22.5, 33.1, -15.6],
  AE: [51.5, 22.6, 56.4, 26.1],
};

function extractGeojsonCoordinates(geojson: unknown): [number, number] | null {
  let feature = geojson as GeoJsonFeature | null;
  if (typeof feature === 'string') {
    try {
      feature = JSON.parse(feature) as GeoJsonFeature;
    } catch {
      return null;
    }
  }
  const coordinates = feature?.geometry?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  const [lon, lat] = coordinates as [number, number];
  if (
    typeof lon !== 'number' ||
    typeof lat !== 'number' ||
    Number.isNaN(lon) ||
    Number.isNaN(lat)
  )
    return null;
  if (lon < -180 || lon > 180 || lat < -90 || lat > 90) return null;
  return [lon, lat];
}

export function isGeojsonWithinCountry(
  countryCode: string,
  geojson: unknown,
): boolean | null {
  const box = COUNTRY_BOUNDING_BOXES[countryCode];
  if (!box) return null;
  const coordinates = extractGeojsonCoordinates(geojson);
  if (!coordinates) return null;
  const [lon, lat] = coordinates;
  const [west, south, east, north] = box;
  const pad = GEO_BOUNDING_BOX_PADDING_DEG;
  return (
    lon >= west - pad &&
    lon <= east + pad &&
    lat >= south - pad &&
    lat <= north + pad
  );
}

export function resolveCountryGeojson(
  countryCode: string,
  geojson: unknown,
): unknown {
  const centroid = COUNTRIES_GEO_LOCATION[countryCode];
  if (!extractGeojsonCoordinates(centroid)) return geojson;
  if (!geojson) return centroid;
  if (isGeojsonWithinCountry(countryCode, geojson) === false) return centroid;
  return geojson;
}

/**
 * The internal "house" company representing Retraced itself. Excluded from
 * admin-facing company pickers (e.g. the available-companies-for-user list)
 * since it's not a real customer company a user should be added to.
 */
export const RETRACED_COMPANY_ID = '9edf3d15-e68d-48a5-a0d0-3e21a3fe86a8';

/**
 * Roles whose holders act as Retraced staff rather than as a customer company:
 * they may read and edit any company's data regardless of network links.
 * Derived from the canonical `RETRACED_ROLE_KEYS` (`modules/permissions`) so
 * this list can't drift from what the retraced tenant's `iam_roles` actually
 * contains.
 */
export const RETRACED_ADMIN_ROLES = Object.keys(RETRACED_ROLE_KEYS);
